import { z } from 'zod';
import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import * as FlatModels from "@medsurf/flat-models";
import * as FlatFacades from '@medsurf/flat-facades';
import { ZodSharedValidator } from "@medsurf/flat-validators";

@Component({
    selector: 'medsurf-slide-edit-marker-targets-form',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './marker-targets-form.component.html',
    styleUrls: ['./marker-targets-form.component.scss'],
    standalone: false
})
export class MarkerTargetsFormComponent {
  #isSubmitted = signal(false);
  #marker: FlatModels.AnnotationEntityModels.Marker;
  #annotationTargets: FlatModels.AnnotationTargetEntityModels.AnnotationTarget[];
  annotationTargetsForm = new FormGroup(
    {
      targets: new FormControl(0),
      color: new FormControl(''),
      endType: new FormControl(''),
      endRadius: new FormControl(0),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.AnnotationTargetEntityModels.AnnotationTargetSchema.pick({
          color: true,
          endType: true,
          endRadius: true
        }).merge(
          z.object({
            targets: z.number(),
          })
        )
      )
    }
  );
  readonly FlatModels = FlatModels;
  
  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public slideAuthorFacade: FlatFacades.SlideAuthorFacade) {
    // Update Marker
    this.slideAuthorFacade.currentSelectedSlideAnnotation$.pipe(takeUntilDestroyed()).subscribe((marker) => {
      if (marker) {
        this.#marker = structuredClone(marker) as FlatModels.AnnotationEntityModels.Marker;
      }
    });
    
    // Update Marker Targets
    this.slideAuthorFacade.currentSelectedSlideMarkerTargets$.pipe(takeUntilDestroyed()).subscribe((markerTargets) => {
      this.#annotationTargets = markerTargets;
      if (markerTargets && markerTargets.length > 0) {
        this.annotationTargetsForm.reset({
          targets: markerTargets?.length || 0,
          color: markerTargets[0]?.color,
          endType: markerTargets[0]?.endType,
          endRadius: markerTargets[0]?.endRadius,
        });
      }
      this.#isSubmitted.set(false);
    });
  }
  
  onSubmit() {
    if (this.#isSubmitted() || this.annotationTargetsForm.invalid) {
      return;
    }
    this.#isSubmitted.set(true);
    
    // Targets Changes
    if ((this.annotationTargetsForm.value.targets || 0) < this.#marker.targets.length) {
      this.slideAuthorFacade.requestUpdateMarkerRemoveTarget(this.#marker).subscribe({
        error: (result) => {
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.annotationTargetsForm);
          this.#isSubmitted.set(false);
        },
        next: () => {
          this.#isSubmitted.set(false);
        }
      });
    } else if ((this.annotationTargetsForm.value.targets || 0) > this.#marker.targets.length) {
      this.slideAuthorFacade.requestUpdateMarkerAddTargetWithPosition(this.#marker).subscribe({
        error: (result) => {
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.annotationTargetsForm);
          this.#isSubmitted.set(false);
        },
        next: () => {
          this.#isSubmitted.set(false);
        }
      });
    }
    
    // Targets Changes
    if (this.annotationTargetsForm.value.color !== this.#annotationTargets[0]?.color
      || this.annotationTargetsForm.value.endType !== this.#annotationTargets[0]?.endType
      || this.annotationTargetsForm.value.endRadius !== this.#annotationTargets[0]?.endRadius) {
      this.slideAuthorFacade.requestUpdateMarkerTargets(this.#marker,
        this.annotationTargetsForm.value.color || '',
        this.annotationTargetsForm.value.endType as FlatModels.AnnotationEntityModels.TipType,
        this.annotationTargetsForm.value.endRadius || 0).subscribe({
        error: (result) => {
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.annotationTargetsForm);
          this.#isSubmitted.set(false);
        },
        next: () => {
          this.#isSubmitted.set(false);
        }
      });
    }
  }
  
  onEndTypeChange() {
    if (this.annotationTargetsForm.value.endType === FlatModels.AnnotationEntityModels.TipType.enum.circle && (!this.annotationTargetsForm.value.endRadius || this.annotationTargetsForm.value.endRadius === 0)) {
      this.annotationTargetsForm.patchValue({endRadius: 20});
    }
    this.onSubmit();
  }
}
